import { z } from 'zod';
import { DbJsonSchema } from '../../dbJsonSchemas.js';
import { allowedFilename } from '../../uploadMedia.js';
import { enumToZodLiteralUnion, helperSchema } from '../../zodExtensions.js';
import { exchangeRecommendationQuery, productVariantExchangeResponse, } from '../customer/schema/variantExchange.js';
import { claimCreate, claimFinalizePayload, claimNotifyByEmail, } from '../schema/claim.js';
import { ReportableEntity } from '../../enums/crew.js';
import { ClaimStatusEnum } from '../../enums/gsp.js';
import { locationAddress, verifyAddressResponse } from '../schema/address.js';
import { claimCalculation } from '../schema/claimCalculation.js';
import { reportDefinitionCreateRequest, reportDefinitionResponse, reportDefinitionUpdateRequest, reportInlineRunRequest, storeUiView, storeUiViewCreate, } from '../schema/filters.js';
import { crewOrder } from '../schema/order.js';
import { shippingClaimLookup } from '../schema/shippingClaim/shippingClaimAutomation.js';
import { Sidekick } from '../schema/sidekick.js';
import { storeDiscountDefinition } from '../schema/storeDiscountDefinition.js';
import { apiClientResp } from './error.js';
import { CrewMerchantUi } from './schema.js';
import { analyticsCatalogResponse, analyticsListRequest, analyticsListResponse, analyticsQueryRequest, analyticsQueryResponse, } from './schema/analytics.js';
import { ConfigureShippingMethod } from './schema/configure.js';
import { configWebhook, configWebhookCreate, configWebhookDelivery, configWebhookDeliverySearchReq, configWebhookDeliverySearchResp, configWebhookEvent, configWebhookEventUpdateBody, configWebhookUpdate, } from './schema/configWebhook.js';
//* user
const userPath = `/user`;
const userTokenLoginPath = `${userPath}/token/login`;
const userIdFromPlatformPath = `${userPath}/:idFromPlatform`;
const userParams = z.object({ idFromPlatform: z.string() });
// user-scoped, not store-scoped: an MCP identity grant belongs to the login, and spans every store it can reach
const userMcpIdentityGrantPath = `${userIdFromPlatformPath}/mcp-identity-grant`;
const userMcpIdentityGrantIdPath = `${userMcpIdentityGrantPath}/:identityGrantId`;
const userMcpIdentityGrantParams = userParams.extend({
    identityGrantId: z.coerce.number(),
});
const merchantProtectSetPath = '/merchant-paid-protect-set';
const userAccessCodePath = `/users`;
const userAccessCodeQuery = z.object({ accessCode: z.string() });
const signedUrlPath = `/signed-url`;
const storeIdPath = `/:storeId`;
const storeIdParams = z.object({ storeId: z.coerce.number() });
const storeIdSearchPath = `${storeIdPath}/search`;
const storeIdAnalyticsPath = `${storeIdPath}/analytics`;
const storeIdAnalyticsCatalogPath = `${storeIdAnalyticsPath}/catalog`;
const storeIdAnalyticsQueryPath = `${storeIdAnalyticsPath}/query`;
const storeIdAnalyticsListQueryPath = `${storeIdAnalyticsPath}/lists/query`;
const storeIdGenReportPath = `${storeIdPath}/report`;
const conciergeSearchPath = `/concierge/search`;
const conciergeCustomerPath = `/concierge/customer`;
const storeIdFrontReportPath = `${storeIdGenReportPath}/front`;
const reportableEntityEnum = z.enum(ReportableEntity);
const reportsPath = `${storeIdPath}/reports`;
const reportInlineRunPath = `${reportsPath}/run`;
const reportDefinitionsListPath = `${reportsPath}/:entity/definitions`;
const reportDefinitionIdPath = `${reportDefinitionsListPath}/:definitionId`;
const reportDefinitionRunPath = `${reportsPath}/definitions/:definitionId/run`;
const reportEntityParams = z
    .object({ entity: reportableEntityEnum })
    .merge(storeIdParams);
const reportDefinitionIdParams = z
    .object({ definitionId: z.coerce.number() })
    .merge(reportEntityParams);
const reportDefinitionRunParams = z
    .object({ definitionId: z.coerce.number() })
    .merge(storeIdParams);
const storeIdTagPath = `${storeIdPath}/tag/:tagId`;
const storeIdViewPath = `${storeIdPath}/view`;
const storeIdViewIdPath = `${storeIdViewPath}/:viewId`;
const storeIdViewIdParams = z
    .object({ viewId: z.coerce.number() })
    .merge(storeIdParams);
const storeIdTagParams = z
    .object({ tagId: z.coerce.number() })
    .merge(storeIdParams);
const storeIdShippingProtectionPath = `${storeIdPath}/shipping-protection`;
const shippingProtectionConfigurePath = `${storeIdShippingProtectionPath}/configure`;
const shippingProtectionCheckoutPlusEmbedPath = `${storeIdShippingProtectionPath}/checkout-plus-embed`;
const shippingProtectionRatePath = `${storeIdShippingProtectionPath}/rate`;
const shippingProtectionRateIdPath = `${shippingProtectionRatePath}/:rateId`;
const shippingProtectionRateIdParams = storeIdParams.merge(z.object({ rateId: z.coerce.number() }));
const shippingClaimPath = `${storeIdShippingProtectionPath}/claim`;
const registrationPath = `${storeIdPath}/registration`;
const registrationIdPath = `${registrationPath}/:registrationId`;
const registrationIdRestorePath = `${registrationIdPath}/restore`;
const storeRegistrationTagPath = `${registrationPath}/tags`;
const storeRegistrationTagIdPath = `${storeRegistrationTagPath}/:tagId`;
const registrationIdTagPath = `${registrationIdPath}/tags`;
const registrationIdTimelinePath = `${registrationIdPath}/timeline`;
const storeIdProductIdFromPlatformParams = z
    .object({ idFromPlatform: z.coerce.number() })
    .merge(storeIdParams);
const storeProductDetailParams = z
    .object({ storeProductId: z.coerce.number() })
    .merge(storeIdParams);
const storeCustomFieldPath = `${storeIdPath}/custom-field`;
const storeCustomFieldIdPath = `${storeCustomFieldPath}/:customFieldId`;
const storeMetafieldPath = `${storeIdPath}/metafield`;
const storeMetafieldPlatformPath = `${storeMetafieldPath}/platform`;
const storeMetafieldIdPath = `${storeMetafieldPath}/:metafieldId`;
const storeRulePath = `${storeIdPath}/automation`;
const storeRuleIdPath = `${storeRulePath}/:automationId`;
const factValuesPath = `${storeIdPath}/fact/values`;
const invoicePath = `${storeIdPath}/invoice`;
const customerPaymentPath = `${storeIdPath}/customer-payment`;
const customerPaymentIdPath = `${customerPaymentPath}/:customerPaymentId`;
const customerPaymentIdRefundPath = `${customerPaymentIdPath}/refund`;
const settingsPath = `${storeIdPath}/settings`;
const settingsThemePath = `${settingsPath}/theme`;
const settingsConfigPath = `${settingsPath}/config`;
const settingsIntegrationPath = `${settingsPath}/integrations`;
const settingsIdentityGrantPath = `${settingsPath}/identity-grant`;
const settingsIdentityGrantIdPath = `${settingsIdentityGrantPath}/:identityGrantId`;
// webhooks
const settingsWebhooksPath = `${settingsPath}/webhooks`;
const settingsWebhookIdPath = `${settingsWebhooksPath}/:webhookId`;
const settingsWebhookDeliveriesPath = `${settingsWebhookIdPath}/deliveries`;
const settingsWebhookDeliverySearchPath = `${settingsWebhookDeliveriesPath}/search`;
const settingsWebhookDeliveryRetryPath = `${settingsWebhookDeliveriesPath}/:deliveryId/retry`;
const settingsWebhookEventIdPath = `${settingsWebhookIdPath}/events/:eventId`;
const settingsShippingProviderRefreshPath = `${settingsIntegrationPath}/shipping-provider/refresh`;
const settingsKlaviyoTestPath = `${settingsIntegrationPath}/klaviyo/test`;
const settingsStripePath = `${settingsIntegrationPath}/stripe`;
const orderPath = `${storeIdPath}/order`;
const orderIdPath = `${orderPath}/:storeOrderId`;
const orderIdTrackingPath = `${orderIdPath}/tracking`;
const storeOrderIdLineItemIdPath = `${orderIdPath}/line-item/:lineItemId`;
// * return + warranty claims
const claimPath = `${storeIdPath}/claim`;
const storeClaimTagPath = `${claimPath}/tags`;
const storeClaimTagIdPath = `${storeClaimTagPath}/:tagId`;
const customerPath = `${storeIdPath}/customer`;
const claimIdPath = `${claimPath}/:claimId`;
const claimIdFeesPath = `${claimIdPath}/fees`;
const claimIdRiskPath = `${claimIdPath}/risk`;
const claimIdCustomerInfoPath = `${claimIdPath}/customer-info`;
const claimIdTagPath = `${claimIdPath}/tags`;
const claimIdTimelinePath = `${claimIdPath}/timeline`;
const claimIdLedgerPath = `${claimIdPath}/ledger`;
const claimIdOrderLineagePath = `${claimIdPath}/order-lineage`;
const claimIdFinalizePath = `${claimIdPath}/finalize`;
const claimIdCancelPath = `${claimIdPath}/cancel`;
const claimIdReopenPath = `${claimIdPath}/reopen`;
const claimIdShipmentPath = `${claimIdPath}/shipment`;
const claimIdShipmentQuotePath = `${claimIdShipmentPath}/quote`;
const claimIdQuoteAndCreatePath = `${claimIdShipmentPath}/quote-and-create`;
const claimIdNeedsMoreInfoPath = `${claimIdPath}/request-info`;
const claimLineItemPath = `${claimIdPath}/line-item`;
const claimLineItemIdPath = `${claimLineItemPath}/:claimLineItemId`;
const claimLineItemIdFeePath = `${claimLineItemIdPath}/fee/:claimLineItemFeeId`;
const claimLineItemIdMediaPath = `${claimLineItemIdPath}/media`;
const claimLineItemMediaDeletePath = `${claimLineItemIdMediaPath}/:claimLineItemImageId`;
const claimLineItemIdInspectionPath = `${claimLineItemIdPath}/inspection`;
const claimLineItemIdInspectionIdPath = `${claimLineItemIdInspectionPath}/:claimInspectionId`;
const claimLineItemIdInspectionSettingsPath = `${claimLineItemIdInspectionPath}/settings`;
// * shipping claim
const shippingClaimIdPath = `${shippingClaimPath}/:shippingClaimId`;
const shippingClaimLookupPath = `${shippingClaimIdPath}/lookup`;
const shippingClaimIdFinalizePath = `${shippingClaimIdPath}/finalize`;
const shippingClaimIdClosePath = `${shippingClaimIdPath}/close`;
const shippingClaimIdManualRefundPath = `${shippingClaimIdPath}/manual-refund`;
const shippingClaimIdRefundShippingPath = `${shippingClaimIdPath}/refund-shipping`;
const shippingClaimIdManualReorderPath = `${shippingClaimIdPath}/manual-reorder`;
const shippingClaimIdRestorePath = `${shippingClaimIdPath}/restore`;
const shippingClaimIdTimelinePath = `${shippingClaimIdPath}/timeline`;
const shippingClaimIdMessagesPath = `${shippingClaimIdPath}/messages`;
const shippingClaimIdCustomerInfoPath = `${shippingClaimIdPath}/customer-info`;
const shippingClaimLineItemPath = `${shippingClaimIdPath}/line-item`;
const shippingClaimLineItemIdPath = `${shippingClaimLineItemPath}/:shippingClaimLineItemId`;
const shippingClaimReplacementItemIdPath = `${shippingClaimIdPath}/replacement-item/:replacementItemId`;
const shippingClaimIdInventoryPath = `${shippingClaimIdPath}/inventory-levels`;
const shippingClaimIdRiskPath = `${shippingClaimIdPath}/risk`;
const shippingClaimIdTagPath = `${shippingClaimIdPath}/tags`;
const storeShippingClaimTagPath = `${shippingClaimPath}/tags`;
const storeShippingClaimTagIdPath = `${storeShippingClaimTagPath}/:tagId`;
// * shipment policy
const returnLocationPath = `${storeIdPath}/return-location`;
const returnLocationIdPath = `${returnLocationPath}/:returnLocationId`;
const fulfillmentLocationsPath = `${storeIdPath}/fulfillment-location`;
const verifyAddressPath = `${storeIdPath}/verify-address`;
const discountDefinitionPath = `${storeIdPath}/discount-definition`;
const discountDefinitionIdPath = `${discountDefinitionPath}/:storeDiscountDefinitionId`;
// * claim reasons (return +)
const claimReasonPath = `${storeIdPath}/claim-reason`;
const claimReasonIdPath = `${claimReasonPath}/:claimReasonId`;
const claimReasonGroupPath = `${claimReasonPath}/group`;
const claimReasonGroupIdPath = `${claimReasonGroupPath}/:claimReasonGroupId`;
// * store products
const storeProductPath = `${storeIdPath}/product`;
const storeProductExchangePath = `${storeProductPath}/exchange/:idFromPlatform`;
const storeProductTagPath = `${storeProductPath}/tag`;
const storeProductTypePath = `${storeProductPath}/type`;
const storeProductCollectionPath = `${storeProductPath}/collection`;
const storeProductGroupPath = `${storeProductPath}/group`;
const storeProductSyncPath = `${storeProductPath}/sync`;
const storeProductImportPath = `${storeProductPath}/import`;
const storeProductGroupIdPath = `${storeProductGroupPath}/:groupId`;
const storeProductGroupIdListProductsPath = `${storeProductGroupIdPath}/list-products`;
const storeProductGroupIdRefreshProductsPath = `${storeProductGroupIdPath}/refresh-products`;
// ! must be registered after every literal `/product/*` segment above, or they match as an id
const storeProductDetailPath = `${storeProductPath}/:storeProductId`;
const storeProductDetailGroupPath = `${storeProductDetailPath}/group`;
const storeProductDetailRegistrationChannelPath = `${storeProductDetailPath}/registration-channel`;
// * store users
const storeUserPath = `${storeIdPath}/user`;
const storeUserSignupPath = `${storeUserPath}/sign-up`;
const storeUserIdPath = `${storeUserPath}/:userId`;
const storeUserPasswordResetPath = `${storeUserIdPath}/reset-password`;
const storeIdUserIdConversationPath = `${storeUserIdPath}/conversations`;
const storeIdUserIdParams = storeIdParams.merge(z.object({ userId: z.coerce.number() }));
const storeIdUserIdConversationIdPath = `${storeIdUserIdConversationPath}/:conversationId`;
const storeIdUserIdConversationIdParams = z
    .object({ conversationId: z.coerce.number() })
    .merge(storeIdUserIdParams);
const storeIdUserIdConversationIdMessageIdPath = `${storeIdUserIdConversationIdPath}/message/:messageId`;
const storeIdUserIdConversationIdMessageIdParams = z
    .object({ messageId: z.coerce.number() })
    .merge(storeIdUserIdConversationIdParams);
// ------------------  Schemas  ------------------
const signedUrlQuery = z.object({
    filename: allowedFilename,
    path: z.enum([
        'claimLineItem',
        'claimLineItemInspection',
        'customFieldReferenceImage',
    ]),
});
const storeProductQuery = z.object({
    searchTerm: z.string().optional(),
    take: z.coerce.number().optional(),
    fulfillmentLocationIdFromPlatform: z.string().optional(),
    applyInventory: z.stringbool().optional(),
});
const storeRuleIdParams = z
    .object({ automationId: z.coerce.number() })
    .merge(storeIdParams);
const customFieldIdParams = z
    .object({
    customFieldId: z.coerce.number(),
})
    .merge(storeIdParams);
const metafieldIdParams = z
    .object({ metafieldId: z.coerce.number() })
    .extend(storeIdParams.shape);
const customerRefundIdParams = z
    .object({
    customerPaymentId: z.coerce.number(),
})
    .merge(storeIdParams);
const claimIdParams = z
    .object({ claimId: z.coerce.number() })
    .merge(storeIdParams);
const shippingClaimIdParams = z
    .object({ shippingClaimId: z.coerce.number() })
    .merge(storeIdParams);
const shippingClaimLineItemIdParams = shippingClaimIdParams.extend({
    shippingClaimLineItemId: z.coerce.number(),
});
const shippingClaimReplacementItemIdParams = shippingClaimIdParams.extend({
    replacementItemId: z.string(),
});
const registrationIdParams = z
    .object({ registrationId: z.coerce.number() })
    .merge(storeIdParams);
const claimLineItemIdParams = claimIdParams.merge(z.object({ claimLineItemId: z.coerce.number() }));
const claimLineItemIdInspectionParams = claimLineItemIdParams.merge(z.object({ claimInspectionId: z.coerce.number() }));
const claimLineItemFeeIdParams = claimLineItemIdParams.merge(z.object({ claimLineItemFeeId: z.coerce.number() }));
const claimReasonIdParams = z
    .object({ claimReasonId: z.coerce.number() })
    .merge(storeIdParams);
const claimReasonGroupIdParams = z
    .object({ claimReasonGroupId: z.coerce.number() })
    .merge(storeIdParams);
const returnLocationIdParams = z
    .object({ returnLocationId: z.string() })
    .merge(storeIdParams);
const storeUserIdParams = z
    .object({
    userId: z.coerce.number(),
})
    .merge(storeIdParams);
const storeProductGroupIdParams = z
    .object({
    groupId: z.coerce.number(),
})
    .merge(storeIdParams);
const storeOrderQuery = z.union([
    z.object({
        idFromPlatform: z.string(),
    }),
    z.object({
        emailOrPostalCode: z.string(),
        orderNo: z.string(),
    }),
]);
const storeOrderIdParams = z
    .object({ storeOrderId: z.coerce.number() })
    .merge(storeIdParams);
const storeOrderIdLineItemIdParams = storeOrderIdParams.extend({
    lineItemId: z.coerce.number(),
});
const storeUserIdAlertPath = `${storeUserIdPath}/alert`;
const storeAlertIdPath = `${storeUserIdAlertPath}/:alertId`;
const storeAlertIdParams = z
    .object({ alertId: z.coerce.number(), userId: z.coerce.number() })
    .merge(storeIdParams);
const webhookIdParams = z
    .object({ webhookId: z.coerce.number() })
    .merge(storeIdParams);
const webhookEventIdParams = z
    .object({ eventId: z.coerce.number() })
    .merge(webhookIdParams);
const webhookDeliveryIdParams = z
    .object({ deliveryId: z.coerce.number() })
    .extend(webhookIdParams.shape);
export const crewMerchantStoreAlertApi = {
    [storeUserIdAlertPath]: {
        path: storeUserIdAlertPath,
        get: {
            request: { params: storeUserIdParams },
            response: { body: z.array(CrewMerchantUi.storeAlertSchema) },
        },
        put: {
            request: {
                params: storeUserIdParams,
                body: CrewMerchantUi.storeAlertUpdateSchema,
            },
            response: {
                body: z.array(CrewMerchantUi.storeAlertSchema),
            },
        },
    },
    [storeAlertIdPath]: {
        path: storeAlertIdPath,
        put: {
            request: {
                params: storeAlertIdParams,
                body: CrewMerchantUi.storeAlertUpdateSchema,
            },
            response: { body: CrewMerchantUi.storeAlertSchema },
        },
    },
};
export const crewMerchantRegistrationApi = {
    [registrationIdPath]: {
        path: registrationIdPath,
        get: {
            request: { params: registrationIdParams },
            response: { body: CrewMerchantUi.registrationSchema },
        },
        put: {
            request: {
                params: registrationIdParams,
                body: CrewMerchantUi.registrationUpdateSchema,
            },
            response: { body: CrewMerchantUi.registrationSchema },
        },
        delete: {
            request: { params: registrationIdParams },
            response: { body: apiClientResp },
        },
    },
    [registrationIdRestorePath]: {
        path: registrationIdRestorePath,
        post: {
            request: { params: registrationIdParams },
            response: { body: apiClientResp },
        },
    },
    [storeRegistrationTagPath]: {
        path: storeRegistrationTagPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.registrationTagSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.registrationTagCreateSchema,
            },
            response: { body: CrewMerchantUi.registrationTagSchema },
        },
    },
    [storeRegistrationTagIdPath]: {
        path: storeRegistrationTagIdPath,
        delete: {
            request: { params: storeIdTagParams },
            response: { body: apiClientResp },
        },
    },
    [registrationIdTagPath]: {
        path: registrationIdTagPath,
        get: {
            request: { params: registrationIdParams },
            response: { body: z.array(CrewMerchantUi.registrationTagSchema) },
        },
        put: {
            request: {
                params: registrationIdParams,
                body: z.array(CrewMerchantUi.registrationTagCreateSchema),
            },
            response: { body: z.array(CrewMerchantUi.registrationTagSchema) },
        },
    },
    [registrationIdTimelinePath]: {
        path: registrationIdTimelinePath,
        post: {
            request: {
                params: registrationIdParams,
                body: CrewMerchantUi.timelineEventSchema,
            },
            response: { body: apiClientResp },
        },
    },
};
export const crewMerchantDiscountDefinitionApi = {
    [discountDefinitionPath]: {
        path: discountDefinitionPath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: { body: z.array(storeDiscountDefinition) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: storeDiscountDefinition.omit({ id: true }),
            },
            response: { body: storeDiscountDefinition },
        },
    },
    [discountDefinitionIdPath]: {
        path: discountDefinitionIdPath,
        delete: {
            request: {
                params: z
                    .object({ storeDiscountDefinitionId: z.coerce.number() })
                    .merge(storeIdParams),
            },
            response: { body: apiClientResp },
        },
    },
};
export const crewMerchantStoreCustomerApi = {
    [customerPath]: {
        path: customerPath,
        get: {
            request: {
                params: storeIdParams,
                query: z.object({
                    email: z.string(),
                }),
            },
            response: { body: CrewMerchantUi.storeCustomerSchema },
        },
    },
};
export const crewMerchantShippingClaimApi = {
    // ! must stay ahead of `shippingClaimIdPath` in the router, or `tags` reads as a claim id
    [storeShippingClaimTagPath]: {
        path: storeShippingClaimTagPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.shippingClaimTagSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.shippingClaimTagCreateSchema,
            },
            response: { body: CrewMerchantUi.shippingClaimTagSchema },
        },
    },
    [storeShippingClaimTagIdPath]: {
        path: storeShippingClaimTagIdPath,
        delete: {
            request: { params: storeIdTagParams },
            response: { body: apiClientResp },
        },
    },
    [shippingClaimIdTagPath]: {
        path: shippingClaimIdTagPath,
        get: {
            request: { params: shippingClaimIdParams },
            response: { body: z.array(CrewMerchantUi.shippingClaimTagSchema) },
        },
        put: {
            request: {
                params: shippingClaimIdParams,
                body: z.array(CrewMerchantUi.shippingClaimTagCreateSchema),
            },
            response: { body: z.array(CrewMerchantUi.shippingClaimTagSchema) },
        },
    },
    [shippingClaimPath]: {
        path: shippingClaimPath,
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.shippingClaimCreateSchema,
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimLookupPath]: {
        path: shippingClaimLookupPath,
        get: {
            request: { params: shippingClaimIdParams },
            response: { body: shippingClaimLookup },
        },
    },
    [shippingClaimIdPath]: {
        path: shippingClaimIdPath,
        get: {
            request: { params: shippingClaimIdParams },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
        put: {
            request: {
                params: shippingClaimIdParams,
                body: CrewMerchantUi.shippingClaimUpdateSchema,
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimLineItemPath]: {
        path: shippingClaimLineItemPath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: CrewMerchantUi.shippingClaimLineItemAddSchema,
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimLineItemIdPath]: {
        path: shippingClaimLineItemIdPath,
        delete: {
            request: { params: shippingClaimLineItemIdParams },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimReplacementItemIdPath]: {
        path: shippingClaimReplacementItemIdPath,
        delete: {
            request: { params: shippingClaimReplacementItemIdParams },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdInventoryPath]: {
        path: shippingClaimIdInventoryPath,
        get: {
            request: { params: shippingClaimIdParams },
            response: { body: CrewMerchantUi.shippingClaimInventoryLevelsSchema },
        },
    },
    [shippingClaimIdRiskPath]: {
        path: shippingClaimIdRiskPath,
        get: {
            request: { params: shippingClaimIdParams },
            response: {
                body: CrewMerchantUi.shippingClaimRiskAssessmentSchema.nullable(),
            },
        },
    },
    [shippingClaimIdCustomerInfoPath]: {
        path: shippingClaimIdCustomerInfoPath,
        put: {
            request: {
                params: shippingClaimIdParams,
                body: CrewMerchantUi.shippingClaimUpdateCustomerInfoSchema,
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdMessagesPath]: {
        path: shippingClaimIdMessagesPath,
        get: {
            request: { params: shippingClaimIdParams },
            response: { body: CrewMerchantUi.shippingClaimMessagesSchema },
        },
    },
    [shippingClaimIdTimelinePath]: {
        path: shippingClaimIdTimelinePath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: CrewMerchantUi.timelineEventSchema,
            },
            response: { body: apiClientResp },
        },
    },
    [shippingClaimIdFinalizePath]: {
        path: shippingClaimIdFinalizePath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: CrewMerchantUi.shippingClaimFinalizeSchema,
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdClosePath]: {
        path: shippingClaimIdClosePath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: z.object({
                    comment: z.string().optional(),
                    status: z.union([
                        z.literal(ClaimStatusEnum.closedResolved),
                        z.literal(ClaimStatusEnum.closedDenied),
                    ]),
                }),
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdRestorePath]: {
        path: shippingClaimIdRestorePath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: z.object({
                    comment: z.string().optional(),
                }),
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdManualRefundPath]: {
        path: shippingClaimIdManualRefundPath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: z.object({
                    amount: z.number().positive(),
                    comment: z.string().optional(),
                }),
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdManualReorderPath]: {
        path: shippingClaimIdManualReorderPath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: z.object({
                    storeOrderId: z.number(),
                    comment: z.string().optional(),
                }),
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
    [shippingClaimIdRefundShippingPath]: {
        path: shippingClaimIdRefundShippingPath,
        post: {
            request: {
                params: shippingClaimIdParams,
                body: z.object({
                    comment: z.string().optional(),
                }),
            },
            response: { body: CrewMerchantUi.shippingClaimSchema },
        },
    },
};
/** Base CRUD Operations for a Claim */
const crewMerchantClaimCrudApi = {
    [claimPath]: {
        path: claimPath,
        post: {
            request: { params: storeIdParams, body: claimCreate },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdPath]: {
        path: claimIdPath,
        get: {
            request: { params: claimIdParams },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    // runs the calculation live against the claim as it stands
    [claimIdLedgerPath]: {
        path: claimIdLedgerPath,
        get: {
            request: { params: claimIdParams },
            response: { body: claimCalculation },
        },
    },
    [claimIdFinalizePath]: {
        path: claimIdFinalizePath,
        post: {
            request: {
                params: claimIdParams,
                body: z.object({
                    notifyCustomerByEmail: claimNotifyByEmail.optional(),
                    noteToCustomer: helperSchema.optionalizedString,
                    finalizationSource: claimFinalizePayload.shape.finalizationSource,
                }),
            },
        },
    },
    [claimIdCancelPath]: {
        path: claimIdCancelPath,
        post: {
            request: {
                params: claimIdParams,
            },
            response: { body: apiClientResp },
        },
    },
    [claimIdReopenPath]: {
        path: claimIdReopenPath,
        post: {
            request: {
                params: claimIdParams,
            },
            response: { body: apiClientResp },
        },
    },
};
/**
 * Additional Claim APIs for Updating Related Entities or Complex Property Operations
 */
const crewMerchantClaimRelationsApi = {
    [claimIdRiskPath]: {
        path: claimIdRiskPath,
        get: {
            request: {
                params: claimIdParams,
            },
            response: { body: CrewMerchantUi.claimRiskAssessmentSchema.nullable() },
        },
    },
    [claimIdOrderLineagePath]: {
        path: claimIdOrderLineagePath,
        get: {
            request: { params: claimIdParams },
            response: { body: CrewMerchantUi.claimOrderLineageSchema },
        },
    },
    [claimIdCustomerInfoPath]: {
        path: claimIdCustomerInfoPath,
        put: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimUpdateCustomerInfoSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdFeesPath]: {
        path: claimIdFeesPath,
        put: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimUpdateRefundHandlingFeeSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdTagPath]: {
        path: claimIdTagPath,
        get: {
            request: { params: claimIdParams },
            response: { body: z.array(CrewMerchantUi.claimTagSchema) },
        },
        put: {
            request: {
                params: claimIdParams,
                body: z.array(CrewMerchantUi.claimTagCreateSchema),
            },
            response: {
                body: z.array(CrewMerchantUi.claimTagSchema),
            },
        },
    },
    [claimIdNeedsMoreInfoPath]: {
        path: claimIdNeedsMoreInfoPath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimNeedsMoreInfoSchema,
            },
        },
    },
    [claimIdShipmentPath]: {
        path: claimIdShipmentPath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimReturnShipmentCreateSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimIdQuoteAndCreatePath]: {
        path: claimIdQuoteAndCreatePath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimShipmentQuoteAndBuySchema,
            },
            response: { body: apiClientResp },
        },
    },
    [claimIdShipmentQuotePath]: {
        path: claimIdShipmentQuotePath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimReturnShipmentQuoteSchema,
            },
            response: { body: CrewMerchantUi.claimReturnShipmentQuoteResponse },
        },
    },
    [claimLineItemIdMediaPath]: {
        path: claimLineItemIdMediaPath,
        post: {
            request: {
                params: claimLineItemIdParams,
                body: z.object({
                    filename: z.string(),
                }),
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimLineItemMediaDeletePath]: {
        path: claimLineItemMediaDeletePath,
        delete: {
            request: {
                params: z
                    .object({ claimLineItemImageId: z.coerce.number() })
                    .merge(claimLineItemIdParams),
            },
            response: { body: apiClientResp },
        },
    },
    [claimLineItemIdPath]: {
        path: claimLineItemIdPath,
        put: {
            request: {
                params: claimLineItemIdParams,
                body: CrewMerchantUi.claimLineItemUpdateSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimLineItemIdFeePath]: {
        path: claimLineItemIdFeePath,
        put: {
            request: {
                params: claimLineItemFeeIdParams,
                body: CrewMerchantUi.claimLineItemFeeUpdateSchema,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
        delete: {
            request: {
                params: claimLineItemFeeIdParams,
            },
            response: { body: CrewMerchantUi.claimSchema },
        },
    },
    [claimLineItemIdInspectionPath]: {
        path: claimLineItemIdInspectionPath,
        post: {
            request: {
                params: claimLineItemIdParams,
                body: CrewMerchantUi.claimLineItemInspectionCreateSchema,
            },
            response: { body: CrewMerchantUi.claimLineItemInspectionSchema },
        },
    },
    [claimLineItemIdInspectionIdPath]: {
        path: claimLineItemIdInspectionIdPath,
        delete: {
            request: {
                params: claimLineItemIdInspectionParams,
            },
            response: {
                body: apiClientResp,
            },
        },
    },
    [claimIdTimelinePath]: {
        path: claimIdTimelinePath,
        post: {
            request: {
                params: claimIdParams,
                body: CrewMerchantUi.claimTimelineEventSchema,
            },
            response: { body: apiClientResp },
        },
    },
    [claimReasonPath]: {
        path: claimReasonPath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: { body: z.array(CrewMerchantUi.claimReasonSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.claimReasonCreateSchema,
            },
            response: { body: CrewMerchantUi.claimReasonSchema },
        },
    },
    [claimReasonIdPath]: {
        path: claimReasonIdPath,
        put: {
            request: {
                params: claimReasonIdParams,
                body: CrewMerchantUi.claimReasonUpdateSchema,
            },
            response: { body: CrewMerchantUi.claimReasonSchema },
        },
    },
    [claimReasonGroupPath]: {
        path: claimReasonGroupPath,
        get: {
            request: {
                params: storeIdPath,
            },
            response: { body: z.array(CrewMerchantUi.claimReasonGroupSchema) },
        },
        post: {
            request: {
                params: storeIdPath,
                body: CrewMerchantUi.claimReasonGroupCreateSchema,
            },
            response: { body: CrewMerchantUi.claimReasonGroupSchema },
        },
    },
    [claimReasonGroupIdPath]: {
        path: claimReasonGroupIdPath,
        put: {
            request: {
                params: claimReasonGroupIdParams,
                body: CrewMerchantUi.claimReasonGroupUpdateSchema,
            },
            response: { body: CrewMerchantUi.claimReasonGroupSchema },
        },
        delete: {
            request: {
                params: claimReasonGroupIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [claimLineItemIdInspectionSettingsPath]: {
        path: claimLineItemIdInspectionSettingsPath,
        get: {
            request: {
                params: claimLineItemIdParams,
            },
            response: {
                body: CrewMerchantUi.claimInspectionSettingsSchema,
            },
        },
    },
};
/**
 * Merge of claim APIs.
 *
 * Defined with an explicit type annotation.
 * Otherwise, TypeScript will error with TS7056: 'The inferred type of this node exceeds the maximum length the compiler will serialize. An explicit type annotation is needed.'
 *
 * This so a workaround to handle the limit, but we might need to consider a different approach for our APIs leveraging the builder pattern at some point, so the types can be incrementally built/joined together.
 * A few related discussions:
 * - [Zod #1040](https://github.com/colinhacks/zod/issues/1040)
 * - [Comment from Colin McDonnell on Zod #3719](https://github.com/colinhacks/zod/issues/3719#issuecomment-2322247736) noting that Zod 4 will have simplified internals and should help with this a little.
 * - [Suggested Builder Pattern](https://github.com/microsoft/TypeScript/issues/43817#issuecomment-827746462) from [Relevant StackOverflow Answer](https://stackoverflow.com/a/69908599)
 */
export const crewMerchantClaimApi = {
    ...crewMerchantClaimCrudApi,
    ...crewMerchantClaimRelationsApi,
};
const organizationAnalyticsPath = `/organization/:organizationId/analytics`;
const organizationAnalyticsQueryPath = `${organizationAnalyticsPath}/query`;
const organizationAnalyticsListQueryPath = `${organizationAnalyticsPath}/lists/query`;
const organizationAnalyticsCurrenciesPath = `${organizationAnalyticsPath}/currencies`;
// an organization's stores are read one shop currency at a time, so money is never summed across currencies
const organizationAnalyticsQuery = z.object({ currency: z.string() });
const organizationIdParams = z.object({
    organizationId: z.coerce.number(),
});
export const organizationApi = {
    [organizationAnalyticsCurrenciesPath]: {
        path: organizationAnalyticsCurrenciesPath,
        get: {
            request: { params: organizationIdParams },
            response: { body: z.object({ currencies: z.array(z.string()) }) },
        },
    },
    [organizationAnalyticsQueryPath]: {
        path: organizationAnalyticsQueryPath,
        post: {
            request: {
                params: organizationIdParams,
                query: organizationAnalyticsQuery,
                body: analyticsQueryRequest,
            },
            response: { body: analyticsQueryResponse },
        },
    },
    [organizationAnalyticsListQueryPath]: {
        path: organizationAnalyticsListQueryPath,
        post: {
            request: {
                params: organizationIdParams,
                query: organizationAnalyticsQuery,
                body: analyticsListRequest,
            },
            response: { body: analyticsListResponse },
        },
    },
};
// ! These really are more at home at the organizational level, added here initially with future state moving here
// TODO make organization-level API
export const crewMerchantConciergeApi = {
    [conciergeSearchPath]: {
        path: conciergeSearchPath,
        post: {
            request: {
                body: CrewMerchantUi.conciergeSearchReqSchema,
            },
            response: {
                body: CrewMerchantUi.searchRespSchema,
            },
        },
    },
    [conciergeCustomerPath]: {
        path: conciergeCustomerPath,
        get: {
            request: {
                query: z.object({
                    email: z.string(),
                }),
            },
            response: { body: CrewMerchantUi.conciergeCustomerSchema },
        },
    },
};
export const crewMerchantAnalyticsApi = {
    [storeIdAnalyticsCatalogPath]: {
        path: storeIdAnalyticsCatalogPath,
        get: { response: { body: analyticsCatalogResponse } },
    },
    [storeIdAnalyticsQueryPath]: {
        path: storeIdAnalyticsQueryPath,
        post: {
            request: { body: analyticsQueryRequest },
            response: { body: analyticsQueryResponse },
        },
    },
    [storeIdAnalyticsListQueryPath]: {
        path: storeIdAnalyticsListQueryPath,
        post: {
            request: { body: analyticsListRequest },
            response: { body: analyticsListResponse },
        },
    },
};
export const crewMerchantSearchApi = {
    [storeIdSearchPath]: {
        path: storeIdSearchPath,
        post: {
            request: {
                body: CrewMerchantUi.searchReqSchema,
            },
            response: {
                body: CrewMerchantUi.searchRespSchema,
            },
        },
    },
    [reportInlineRunPath]: {
        path: reportInlineRunPath,
        post: {
            request: {
                params: storeIdParams,
                body: reportInlineRunRequest,
            },
            response: { body: apiClientResp },
        },
    },
    [reportDefinitionRunPath]: {
        path: reportDefinitionRunPath,
        post: {
            request: { params: reportDefinitionRunParams },
            response: { body: apiClientResp },
        },
    },
    [reportDefinitionsListPath]: {
        path: reportDefinitionsListPath,
        get: {
            request: { params: reportEntityParams },
            response: { body: z.array(reportDefinitionResponse) },
        },
        post: {
            request: {
                params: reportEntityParams,
                body: reportDefinitionCreateRequest,
            },
            response: { body: reportDefinitionResponse },
        },
    },
    [reportDefinitionIdPath]: {
        path: reportDefinitionIdPath,
        get: {
            request: { params: reportDefinitionIdParams },
            response: { body: reportDefinitionResponse },
        },
        put: {
            request: {
                params: reportDefinitionIdParams,
                body: reportDefinitionUpdateRequest,
            },
            response: { body: reportDefinitionResponse },
        },
        delete: {
            request: { params: reportDefinitionIdParams },
            response: { body: apiClientResp },
        },
    },
    [storeIdFrontReportPath]: {
        path: storeIdFrontReportPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(DbJsonSchema.FrontReport.reportRawSchema) },
        },
    },
    [storeIdViewPath]: {
        path: storeIdViewPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(storeUiView) },
        },
        post: {
            request: { params: storeIdParams, body: storeUiViewCreate },
        },
    },
    [storeIdViewIdPath]: {
        path: storeIdViewIdPath,
        get: {
            request: { params: storeIdViewIdParams },
            response: { body: storeUiView },
        },
        delete: {
            request: { params: storeIdViewIdParams },
            response: { body: apiClientResp },
        },
        put: {
            request: { params: storeIdViewIdParams, body: storeUiView },
            response: { body: storeUiView },
        },
    },
    [storeIdTagPath]: {
        path: storeIdTagPath,
        delete: {
            request: { params: storeIdTagParams },
            response: { body: apiClientResp },
        },
    },
    [userAccessCodePath]: {
        path: userAccessCodePath,
        get: {
            request: { query: userAccessCodeQuery },
            response: { body: CrewMerchantUi.userAccessCodeSchema },
        },
    },
    [signedUrlPath]: {
        path: signedUrlPath,
        get: {
            request: { query: signedUrlQuery },
            response: { body: CrewMerchantUi.signedUrlSchema },
        },
    },
    [customerPaymentIdRefundPath]: {
        path: customerPaymentIdRefundPath,
        post: {
            request: {
                params: customerRefundIdParams,
            },
            response: { body: CrewMerchantUi.claimCustomerPaymentSchema },
        },
    },
};
export const crewMerchantFactValuesApi = {
    [factValuesPath]: {
        path: factValuesPath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.arrayFactDataSchema },
        },
    },
};
export const crewMerchantStoreRulesListApi = {
    [storeRulePath]: {
        path: storeRulePath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.storeRuleSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.storeRuleCreateSchema,
            },
            response: { body: CrewMerchantUi.storeRuleSchema },
        },
    },
};
export const crewMerchantStoreRuleApi = {
    [storeRuleIdPath]: {
        path: storeRuleIdPath,
        get: {
            request: { params: storeRuleIdParams },
            response: { body: CrewMerchantUi.storeRuleSchema },
        },
        put: {
            request: {
                params: storeRuleIdParams,
                body: CrewMerchantUi.storeRuleUpdateSchema,
            },
            response: { body: CrewMerchantUi.storeRuleSchema },
        },
        delete: {
            request: {
                params: storeRuleIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
};
export const crewMerchantDeveloperApi = {
    [settingsIdentityGrantPath]: {
        path: settingsIdentityGrantPath,
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.storeIdentityGrantCreateSchema,
            },
            response: {
                body: CrewMerchantUi.storeIdentityGrantWithClientSecretSchema,
            },
        },
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.storeIdentityGrantSchema) },
        },
    },
    [settingsIdentityGrantIdPath]: {
        path: settingsIdentityGrantIdPath,
        delete: {
            request: {
                params: storeIdParams.merge(z.object({ identityGrantId: z.coerce.number() })),
            },
            response: { body: apiClientResp },
        },
    },
    [settingsWebhooksPath]: {
        path: settingsWebhooksPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(configWebhook) },
        },
        post: {
            request: { params: storeIdParams, body: configWebhookCreate },
            response: { body: configWebhook },
        },
    },
    [settingsWebhookIdPath]: {
        path: settingsWebhookIdPath,
        get: {
            request: { params: webhookIdParams },
            response: { body: configWebhook },
        },
        put: {
            request: { params: webhookIdParams, body: configWebhookUpdate },
            response: { body: configWebhook },
        },
        delete: {
            request: { params: webhookIdParams },
            response: { body: apiClientResp },
        },
    },
    // POST, because a GET query string cannot carry the nested cursor or the array filters —
    // `api.ts` stringifies query params shallowly. Same reason `/:storeId/search` is a POST.
    [settingsWebhookDeliverySearchPath]: {
        path: settingsWebhookDeliverySearchPath,
        post: {
            request: {
                params: webhookIdParams,
                body: configWebhookDeliverySearchReq,
            },
            response: { body: configWebhookDeliverySearchResp },
        },
    },
    [settingsWebhookDeliveryRetryPath]: {
        path: settingsWebhookDeliveryRetryPath,
        post: {
            request: { params: webhookDeliveryIdParams },
            response: { body: configWebhookDelivery },
        },
    },
    [settingsWebhookEventIdPath]: {
        path: settingsWebhookEventIdPath,
        put: {
            request: {
                params: webhookEventIdParams,
                body: configWebhookEventUpdateBody,
            },
            response: { body: configWebhookEvent },
        },
    },
};
export const crewMerchantApi = {
    [storeIdPath]: {
        path: storeIdPath,
        use: storeIdParams,
    },
    [shippingProtectionRatePath]: {
        path: shippingProtectionRatePath,
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.protectionRateCreateSchema,
            },
            response: {
                body: CrewMerchantUi.protectionRateSchema,
            },
        },
        get: {
            request: {
                params: storeIdParams,
            },
            response: {
                body: CrewMerchantUi.protectionRateSchema.array(),
            },
        },
    },
    [shippingProtectionRateIdPath]: {
        path: shippingProtectionRateIdPath,
        put: {
            request: {
                params: shippingProtectionRateIdParams,
                body: CrewMerchantUi.protectionRateUpdateSchema,
            },
            response: {
                body: CrewMerchantUi.protectionRateSchema,
            },
        },
        delete: {
            request: {
                params: shippingProtectionRateIdParams,
            },
            response: {
                body: apiClientResp,
            },
        },
    },
    [shippingProtectionConfigurePath]: {
        path: shippingProtectionConfigurePath,
        post: {
            request: {
                body: z.object({
                    method: enumToZodLiteralUnion(ConfigureShippingMethod),
                }),
            },
            response: {
                body: CrewMerchantUi.shipProtectSettingsSchema,
            },
        },
    },
    [shippingProtectionCheckoutPlusEmbedPath]: {
        path: shippingProtectionCheckoutPlusEmbedPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.object({ isActive: z.boolean() }) },
        },
    },
    [invoicePath]: {
        path: invoicePath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.invoiceSchema.array() },
        },
    },
    [orderPath]: {
        path: orderPath,
        get: {
            request: { params: storeIdParams, query: storeOrderQuery },
            response: { body: crewOrder },
        },
    },
    [orderIdTrackingPath]: {
        path: orderIdTrackingPath,
        get: {
            request: { params: storeOrderIdParams },
            response: { body: CrewMerchantUi.orderShipmentsSchema },
        },
    },
    [storeOrderIdLineItemIdPath]: {
        path: storeOrderIdLineItemIdPath,
        put: {
            request: {
                params: storeOrderIdLineItemIdParams,
                body: z.object({
                    madeIneligibleOn: z.string().nullable(),
                }),
            },
            response: { body: apiClientResp },
        },
    },
    [userIdFromPlatformPath]: {
        path: userIdFromPlatformPath,
        get: {
            request: { params: userParams },
            response: { body: CrewMerchantUi.userWithStoreUserRolesSchema },
        },
    },
    [userMcpIdentityGrantPath]: {
        path: userMcpIdentityGrantPath,
        get: {
            request: { params: userParams },
            response: { body: CrewMerchantUi.userMcpIdentityGrantsSchema },
        },
    },
    [userMcpIdentityGrantIdPath]: {
        path: userMcpIdentityGrantIdPath,
        delete: {
            request: { params: userMcpIdentityGrantParams },
            response: { body: apiClientResp },
        },
    },
    [merchantProtectSetPath]: {
        path: merchantProtectSetPath,
        get: {
            request: {},
            response: {
                body: z.array(z.object({
                    id: z.number(),
                    chargeAmount: z.number(),
                })),
            },
        },
    },
    [userTokenLoginPath]: {
        path: userTokenLoginPath,
        get: {
            request: {
                query: CrewMerchantUi.tokenAuthSchema,
            },
            response: {
                body: z.object({
                    user: CrewMerchantUi.userWithStoreUserRolesSchema,
                    // signed session token the widget UI carries as a Bearer, since the
                    // Kustomer/Zendesk flow has no Auth0 token
                    sessionToken: z.string(),
                }),
            },
        },
    },
    [storeCustomFieldPath]: {
        path: storeCustomFieldPath,
        get: {
            request: {},
            response: { body: z.array(CrewMerchantUi.customFieldSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.customFieldCreateSchema,
            },
            response: { body: CrewMerchantUi.customFieldSchema },
        },
    },
    [storeCustomFieldIdPath]: {
        path: storeCustomFieldIdPath,
        get: {
            request: { params: customFieldIdParams },
            response: { body: CrewMerchantUi.customFieldSchema },
        },
        put: {
            request: {
                params: customFieldIdParams,
                body: CrewMerchantUi.customFieldUpdateSchema,
            },
            response: { body: CrewMerchantUi.customFieldSchema },
        },
        delete: {
            request: {
                params: customFieldIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [storeMetafieldPath]: {
        path: storeMetafieldPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.storeMetafieldSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.storeMetafieldCreateSchema,
            },
            response: { body: CrewMerchantUi.storeMetafieldSchema },
        },
    },
    [storeMetafieldPlatformPath]: {
        path: storeMetafieldPlatformPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.platformMetafieldSchema) },
        },
    },
    [storeMetafieldIdPath]: {
        path: storeMetafieldIdPath,
        delete: {
            request: { params: metafieldIdParams },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [storeClaimTagPath]: {
        path: storeClaimTagPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.claimTagSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.claimTagCreateSchema,
            },
            response: { body: CrewMerchantUi.claimTagSchema },
        },
    },
    [storeClaimTagIdPath]: {
        path: storeClaimTagIdPath,
        delete: {
            request: { params: storeIdTagParams },
            response: { body: apiClientResp },
        },
    },
    [settingsThemePath]: {
        path: settingsThemePath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.settingsThemeSchema },
        },
        put: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.settingsThemeSchema,
            },
            response: { body: CrewMerchantUi.settingsThemeSchema },
        },
    },
    [settingsIntegrationPath]: {
        path: settingsIntegrationPath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.settingsIntegrationSchema },
        },
        put: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.settingsIntegrationUpdateSchema,
            },
            response: { body: CrewMerchantUi.settingsIntegrationSchema },
        },
    },
    [settingsKlaviyoTestPath]: {
        path: settingsKlaviyoTestPath,
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.klaviyoTestEventSchema,
            },
            response: { body: CrewMerchantUi.klaviyoTestEventResponseSchema },
        },
    },
    [settingsStripePath]: {
        path: settingsStripePath,
        delete: {
            request: { params: storeIdParams },
            response: { body: apiClientResp },
        },
    },
    [settingsShippingProviderRefreshPath]: {
        path: settingsShippingProviderRefreshPath,
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.shippingProviderRefreshSchema,
            },
            response: { body: CrewMerchantUi.shippingProviderSchema },
        },
    },
    [settingsConfigPath]: {
        path: settingsConfigPath,
        get: {
            request: { params: storeIdParams },
            response: { body: CrewMerchantUi.settingsConfigSchema },
        },
        put: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.settingsConfigSchema,
            },
            response: { body: CrewMerchantUi.settingsConfigSchema },
        },
    },
    [storeProductPath]: {
        path: storeProductPath,
        get: {
            request: { params: storeIdParams, query: storeProductQuery },
            response: { body: z.array(CrewMerchantUi.storeProductSchema) },
        },
    },
    [storeProductSyncPath]: {
        path: storeProductSyncPath,
        post: {
            request: { params: storeIdParams },
            response: { body: apiClientResp },
        },
    },
    [storeProductImportPath]: {
        path: storeProductImportPath,
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.storeProductImportSchema,
            },
            response: { body: CrewMerchantUi.storeProductImportAcceptedSchema },
        },
    },
    [storeProductExchangePath]: {
        path: storeProductExchangePath,
        get: {
            request: {
                params: storeIdProductIdFromPlatformParams,
                query: exchangeRecommendationQuery,
            },
            response: { body: productVariantExchangeResponse },
        },
    },
    [storeProductDetailPath]: {
        path: storeProductDetailPath,
        get: {
            request: { params: storeProductDetailParams },
            response: { body: CrewMerchantUi.storeProductDetailSchema },
        },
        put: {
            request: {
                params: storeProductDetailParams,
                body: CrewMerchantUi.storeProductUpdateSchema,
            },
            response: { body: CrewMerchantUi.storeProductDetailSchema },
        },
        delete: {
            request: { params: storeProductDetailParams },
            response: { body: apiClientResp },
        },
    },
    [storeProductDetailGroupPath]: {
        path: storeProductDetailGroupPath,
        get: {
            request: { params: storeProductDetailParams },
            response: { body: CrewMerchantUi.storeProductGroupMembershipSchema },
        },
    },
    [storeProductDetailRegistrationChannelPath]: {
        path: storeProductDetailRegistrationChannelPath,
        get: {
            request: { params: storeProductDetailParams },
            response: {
                body: CrewMerchantUi.storeProductRegistrationChannelsSchema,
            },
        },
    },
    [storeProductTagPath]: {
        path: storeProductTagPath,
        get: {
            request: { params: storeIdParams },
            response: {
                body: z.array(CrewMerchantUi.storeProductTagSchema),
            },
        },
    },
    [storeProductCollectionPath]: {
        path: storeProductCollectionPath,
        get: {
            request: { params: storeIdParams },
            response: {
                body: z.array(CrewMerchantUi.storeProductCollectionSchema),
            },
        },
    },
    [storeProductTypePath]: {
        path: storeProductTypePath,
        get: {
            request: { params: storeIdParams },
            response: {
                body: z.array(CrewMerchantUi.storeProductTypeSchema),
            },
        },
    },
    [storeProductGroupPath]: {
        path: storeProductGroupPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.storeProductGroupSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.storeProductGroupCreateSchema,
            },
            response: { body: CrewMerchantUi.storeProductGroupSchema },
        },
    },
    [storeProductGroupIdPath]: {
        path: storeProductGroupIdPath,
        put: {
            request: {
                params: storeProductGroupIdParams,
                body: CrewMerchantUi.storeProductGroupUpdateSchema,
            },
            response: { body: CrewMerchantUi.storeProductGroupSchema },
        },
        delete: {
            request: {
                params: storeProductGroupIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [storeProductGroupIdListProductsPath]: {
        path: storeProductGroupIdListProductsPath,
        get: {
            request: {
                params: storeProductGroupIdParams,
            },
            response: { body: z.array(CrewMerchantUi.storeProductsForGroupSchema) },
        },
    },
    [storeProductGroupIdRefreshProductsPath]: {
        path: storeProductGroupIdRefreshProductsPath,
        post: {
            request: {
                params: storeProductGroupIdParams,
            },
            response: { body: apiClientResp },
        },
    },
    [returnLocationPath]: {
        path: returnLocationPath,
        get: {
            request: { params: storeIdParams },
            response: { body: z.array(CrewMerchantUi.returnLocationSchema) },
        },
        post: {
            request: {
                params: storeIdParams,
                body: CrewMerchantUi.returnLocationCreateSchema,
            },
            response: { body: CrewMerchantUi.returnLocationSchema },
        },
    },
    [returnLocationIdPath]: {
        path: returnLocationIdPath,
        put: {
            request: {
                params: returnLocationIdParams,
                body: CrewMerchantUi.returnLocationSchema,
            },
            response: { body: CrewMerchantUi.returnLocationSchema },
        },
        delete: {
            request: {
                params: returnLocationIdParams,
            },
            response: { body: CrewMerchantUi.apiClientRespSchema },
        },
    },
    [verifyAddressPath]: {
        path: verifyAddressPath,
        post: {
            request: { params: storeIdParams, body: locationAddress },
            response: { body: verifyAddressResponse },
        },
    },
    [fulfillmentLocationsPath]: {
        path: fulfillmentLocationsPath,
        get: {
            request: { params: storeIdParams },
            response: {
                body: z.array(z.object({
                    id: z.number(),
                    name: z.string(),
                    idFromPlatform: z.string(),
                    isCorsoReorderEnabled: z.boolean(),
                })),
            },
        },
    },
    [storeUserPath]: {
        path: storeUserPath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: {
                body: z.array(CrewMerchantUi.storeUserSchema),
            },
        },
        post: {
            request: {
                body: CrewMerchantUi.storeUserCreateSchema,
                params: storeIdParams,
            },
            response: {
                body: CrewMerchantUi.storeUserSchema,
            },
        },
    },
    [storeUserSignupPath]: {
        path: storeUserSignupPath,
        post: {
            request: {
                query: userAccessCodeQuery,
                body: CrewMerchantUi.storeUserSignupCreateSchema,
                params: storeIdParams,
            },
            response: {
                body: CrewMerchantUi.storeUserSignupCreateSchema,
            },
        },
    },
    [storeUserIdPath]: {
        path: storeUserIdPath,
        put: {
            request: {
                params: storeUserIdParams,
                body: CrewMerchantUi.storeUserUpdateSchema,
            },
            response: {
                body: CrewMerchantUi.storeUserSchema,
            },
        },
        delete: {
            request: {
                params: storeUserIdParams,
            },
            response: {
                body: CrewMerchantUi.apiClientRespSchema,
            },
        },
    },
    [storeUserPasswordResetPath]: {
        path: storeUserPasswordResetPath,
        post: {
            request: {
                params: storeUserIdParams,
                body: CrewMerchantUi.storeUserSchema,
            },
            response: {
                body: CrewMerchantUi.storeUserSchema,
            },
        },
    },
};
// Sidekick, the in-app AI chat: a user's conversations in one store
export const crewMerchantSidekickApi = {
    [storeIdUserIdConversationPath]: {
        path: storeIdUserIdConversationPath,
        get: {
            request: {
                params: storeIdUserIdParams,
            },
            response: {
                body: z.array(Sidekick.conversationSchema),
            },
        },
        post: {
            request: {
                params: storeIdUserIdParams,
            },
            response: {
                body: z.object({ conversationId: z.number() }),
            },
        },
    },
    [storeIdUserIdConversationIdPath]: {
        path: storeIdUserIdConversationIdPath,
        get: {
            request: {
                params: storeIdUserIdConversationIdParams,
            },
            response: {
                body: Sidekick.conversationWithMessagesSchema,
            },
        },
        post: {
            request: {
                params: storeIdUserIdConversationIdParams,
                body: Sidekick.chatRequestSchema,
            },
            // AI SDK UI message stream
            response: { body: z.unknown() },
        },
        put: {
            request: {
                params: storeIdUserIdConversationIdParams,
                body: Sidekick.renameSchema,
            },
            response: { body: Sidekick.conversationSchema },
        },
    },
    [storeIdUserIdConversationIdMessageIdPath]: {
        path: storeIdUserIdConversationIdMessageIdPath,
        post: {
            request: {
                params: storeIdUserIdConversationIdMessageIdParams,
                body: Sidekick.feedbackSchema,
            },
        },
    },
};
//# sourceMappingURL=api.js.map